import { Dayjs } from 'dayjs';

import {
  ActivitySector,
  ActivitySectorHttp,
} from './activity-sector.interface';
import { CityHttp } from './city.interface';
import { DeviceTypeHttp } from './device-type.interface';
import { Qualification, QualificationHttp } from './qualification.interface';
import {
  TrainingCourseCandidate,
  TrainingCourseCandidateHttp,
} from './training-course-candidate.interface';
import { TrainingCourseEducationalPathHttp } from './training-course-educational-path.interface';
import {
  Estimator,
  TrainingCourseEstimatorHttp,
} from './training-course-estimator.interface';
import {
  TrainingCourseGroup,
  TrainingCourseGroupHttp,
} from './training-course-group.interface';
import { TrainingCourseSessionHttp } from './training-course-session.interface';
import { TrainingCourseTrainingHttp } from './training-course-training.interface';
import { TrainingCourseHttp } from './training-course.interface';

export enum TrainingCourseContractStatus {
  DRAFT = 'draft',
  SENT_TO_LUCIE = 'sent_to_lucie',
  SEND_TO_LUCIE = 'send_to_lucie',
}

export type TrainingCourseContractRawIncluded = Array<
  | TrainingCourseGroupHttp
  | TrainingCourseCandidateHttp
  | QualificationHttp
  | ActivitySectorHttp
  | TrainingCourseHttp
  | TrainingCourseTrainingHttp
  | TrainingCourseSessionHttp
  | CityHttp
  | TrainingCourseEstimatorHttp
  | TrainingCourseEducationalPathHttp
  | DeviceTypeHttp
>;

export interface TrainingCourseContractIncluded {
  groups: TrainingCourseGroupHttp[];
  candidates: TrainingCourseCandidateHttp[];
  qualifications: QualificationHttp[];
  sector: ActivitySectorHttp[];
  training_course: TrainingCourseHttp[];
  training: TrainingCourseTrainingHttp[];
  sessions: TrainingCourseSessionHttp[];
  cities: CityHttp[];
  estimators: TrainingCourseEstimatorHttp[];
  educational_paths: TrainingCourseEducationalPathHttp[];
  device_types: DeviceTypeHttp[];
}

export interface TrainingCourseContractHttp {
  type: 'training_course_contracts';
  id: string;
  attributes: {
    company_agency_client_atlas_id: string;
    status: string;
    address: string;
    city: string;
    place: string;
    zip_code: string;
    client_id: string;
    client_name: string;
    client_siret: string;
    client_code: string;
    client_registration_number: string;
    site_company_name: string;
    site_code: string;
    site_city: string;
    site_phone: string;
    site_zip_code: string;
    site_address: string;
    start_date: string;
    end_date: string;
    particular_conditions: string;
    justification: string;
    device_type_initials: string;
    non_working_period: string;
    trial_period: number | undefined;
    deletedAt: string | null;
    createdAt: string;
    updatedAt: string;
  };
  relationships: {
    training_course: {
      data: {
        type: 'training_courses';
        id: string;
      } | null;
    };
    training_course_group: {
      data: {
        type: 'training_course_groups';
        id: string;
      } | null;
    };
    qualification: {
      data: {
        type: 'qualification_repositories';
        id: string;
      } | null;
    };
    candidates: {
      data: {
        type: 'training_course_candidates';
        id: string;
      }[];
    };
    sector: {
      data: {
        type: 'activity_sector_repositories';
        id: string;
      } | null;
    };
    training_course_estimator: {
      data: {
        type: 'training_course_estimators';
        id: string;
      };
    };
  };
}

export interface TrainingCourseContract {
  id: string | null;
  companyAgencyClientAtlasId: string;
  status: TrainingCourseContractStatus;
  address: string;
  city: string;
  place: string;
  zipCode: string;
  clientId: string;
  clientName: string;
  clientSiret: string;
  clientCode: string;
  clientRegistrationNumber: string;
  siteCompanyName: string;
  siteCode: string;
  siteCity: string;
  sitePhone: string;
  siteZipCode: string;
  siteAddress: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  trainingCourseId: string | null;
  trainingCourseGroup?: TrainingCourseGroup;
  qualification?: Qualification;
  justification: string;
  particularConditions: string;
  candidates: TrainingCourseCandidate[];
  sector: ActivitySector;
  trainingCourseEstimator: Estimator;
  deviceTypeInitials: string;
  nonWorkingPeriod: string;
  trialPeriod?: number;
}

export interface TrainingCourseContractWizard {
  type: 'training_course_contracts';
  id: string;
  attributes: {
    company_agency_client_atlas_id: string;
    address: string;
    city: string;
    place: string;
    zip_code: string;
    client_id: string;
    client_name: string;
    client_siret: string;
    client_code: string;
    client_registration_number: string;
    site_company_name: string;
    site_code: string;
    site_city: string;
    site_phone: string;
    site_zip_code: string;
    site_address: string;
    start_date: Dayjs | null;
    end_date: Dayjs | null;
    particular_conditions: string;
    device_type_initials: string;
    non_working_period: string;
    justification: string;
    status: string;
    trial_period: number | undefined;
  };
  relationships: {
    training_course: {
      data: {
        type: 'training_courses';
        id: string;
      } | null;
    };
    training_course_group: {
      data: {
        type: 'training_course_groups';
        id: string;
      } | null;
    };
    candidates: {
      data: {
        type: 'training_course_candidates';
        id: string;
      }[];
    };
    sector?: {
      data: {
        type: 'activity_sector_repositories';
        id: string;
      } | null;
    };
    qualification?: {
      data: {
        type: 'qualification_repositories';
        id: string;
      } | null;
    };
    training_course_estimator?: {
      data: {
        type: 'training_course_estimators';
        id: string;
      } | null;
    };
  };
}
